// components/common/GridExportButton.jsx
//
// The single grid-export affordance across the SaaS app. One element gives a
// grid backend-driven export: an [Export ▼] button whose menu offers CSV / Excel
// for two scopes — the current filter set and the full dataset — and which owns
// the fetch, the busy state and the browser download end to end.
//
//   <GridExportButton
//     endpoint="/api/items/export/"
//     filename="items"
//     getParams={(scope) => scope === 'all' ? '' : `&search=${encodeURIComponent(search)}`}
//   />
//
// Progress and failure reach the merchant as the app toast.
//
// AG Grid's native right-click export is off everywhere; the one export path
// is the backend.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../../services/SessionApiService';
import { apiErrorFrom } from '../../utils/parseApiError';
import { translateRequestError } from '../../queries/api';
import { downloadBlobResponse, exportFilename } from '../../utils/download';
import {
  ExportAbandoned, ExportStillGenerating, awaitExportFile, isAsyncExport,
} from '../../utils/asyncExport';
import { useStoreToday } from '../../hooks/useStoreToday';
import { ICON, ExportIcon } from '../../icons';
import ActionMenu from './ActionMenu';
import { showToast } from './appToastBus';

/**
 * @param {string}   endpoint   API export endpoint, e.g. '/api/items/export/'.
 * @param {string}   filename   Base filename without extension; the store's
 *                              date is appended (utils/download exportFilename).
 * @param {Function} getParams  (scope) => extra query fragment, e.g. '&search=foo'.
 *                              scope is 'filtered' (apply the grid's filters) or
 *                              'all' (base scope only). Callers that don't
 *                              distinguish may ignore the arg.
 * @param {boolean}  [disabled=false]
 * @param {string}   [label]    Trigger text. Defaults to the bare "Export";
 *                              pass a translated string where the page holds
 *                              more than one export and the bare word would
 *                              not say which grid leaves.
 */
export default function GridExportButton({
  endpoint,
  filename,
  getParams,
  disabled = false,
  label,
}) {
  const { t } = useTranslation();
  const storeToday = useStoreToday();

  // A generated export is polled for minutes. If the page goes away while a
  // poll is in flight, the loop must stop and stop alerting — otherwise it
  // keeps a timer alive and raises toasts for a page nobody is looking
  // at. Checked at the one point the loop can be interrupted: the next poll.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // While an export runs — a generated one polls for minutes — the trigger shows
  // MUI's `loading` and is disabled: it must not look idle, and the same export
  // must not be queued twice (hooks/useBlobDownload.js states why). The label
  // never changes, the same rule as a grid row's ⋯ menu (GridRowActions).
  const [busy, setBusy] = useState(false);

  const exportFile = useCallback(async (format, scope) => {
    const extra = getParams ? getParams(scope) : '';
    let res;
    try {
      res = await sessionApi.get(`${endpoint}?file_format=${format}${extra}`);
      if (!res.ok) throw await apiErrorFrom(res, `GET ${endpoint}`);
    } catch (err) {
      console.error('export request failed:', err.message);
      showToast(translateRequestError(err, t, 'export.failed'), 'error');
      return;
    }

    let fileRes = res;
    // The generated file's name comes from the SERVER (see ExportJob.filename)
    // — the direct download's is built here. They differ: a generated file is
    // dated when it was generated, which may not be today.
    let fileName = exportFilename(filename, format, storeToday);
    // Past the server's row threshold the export is generated off-request and
    // this response is a job, not a file. Downloading it as one would save the
    // JSON envelope under the export's filename — a file that opens and is not
    // the export.
    if (isAsyncExport(res)) {
      const job = await res.json();
      showToast(t('export.preparing'), 'info');
      try {
        const ready = await awaitExportFile(job, {
          getJob: async (id) => {
            if (!mounted.current) throw new ExportAbandoned();
            const poll = await sessionApi.get(`/api/export-jobs/${id}/`);
            if (!poll?.ok) throw new Error('export job poll failed');
            return poll.json();
          },
          getFile: (id) => sessionApi.get(`/api/export-jobs/${id}/download/`),
        });
        fileRes = ready.response;
        fileName = ready.job.filename;
      } catch (err) {
        // Nothing to say to a page that is gone; the run continues and the
        // file will be waiting.
        if (!mounted.current) return;
        if (err instanceof ExportStillGenerating) {
          showToast(t('export.stillPreparing'), 'info');
          return;
        }
        console.error('export job failed:', err.message);
        showToast(translateRequestError(err, t, 'export.failed'), 'error');
        return;
      }
      if (!mounted.current) return;
      if (!fileRes.ok) {
        const err = await apiErrorFrom(fileRes, `GET /api/export-jobs/<id>/download/`);
        console.error('export download refused:', err.message);
        showToast(translateRequestError(err, t, 'export.failed'), 'error');
        return;
      }
    }

    await downloadBlobResponse(fileRes, fileName);
  }, [endpoint, filename, getParams, t, storeToday]);

  const run = useCallback(async (format, scope) => {
    setBusy(true);
    try {
      await exportFile(format, scope);
    } finally {
      setBusy(false);
    }
  }, [exportFile]);

  return (
    <ActionMenu
      items={[
        { key: 'filteredCsv', label: t('export.filteredCsv'), onClick: () => run('csv', 'filtered') },
        { key: 'filteredExcel', label: t('export.filteredExcel'), onClick: () => run('xlsx', 'filtered') },
        { key: 'allCsv', label: t('export.allCsv'), onClick: () => run('csv', 'all'), separatorBefore: true },
        { key: 'allExcel', label: t('export.allExcel'), onClick: () => run('xlsx', 'all') },
      ]}
      renderTrigger={(triggerProps) => (
        <Button
          variant="outlined"
          startIcon={<ExportIcon size={ICON.md} />}
          disabled={disabled}
          loading={busy}
          {...triggerProps}
        >
          {label ?? t('export.button')}
        </Button>
      )}
    />
  );
}
